<template>
  <div ref="editorContainer" class="markdown-editor" :style="{ height }"></div>
</template>

<script>
import { uploadFile } from '@/api/more/more'
import Cookies from 'js-cookie'

export default {
  name: 'MarkdownEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    height: {
      type: String,
      default: '300px'
    }
  },
  data() {
    return {
      editor: null,
      isMounted: false
    }
  },
  watch: {
    value(val) {
      if (this.editor && val !== this.editor.getMarkdown()) {
        this.editor.setMarkdown(val)
      }
    },
    height() {
      if (this.editor) {
        this.editor.height(this.height)
      }
    },
    // 监听语言变化，更新tooltip
    'globalDataArray.siteInfo.language'() {
      this.updateTooltip()
    }
  },
  mounted() {
    this.isMounted = true
    if (this.$isServer === false) {
      this.initEditor()
    }

    // 延迟更新tooltip，确保globalDataArray已经加载
    setTimeout(() => {
      this.updateTooltip()
    }, 1000)
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  },
  methods: {
    async initEditor() {
      if (this.$isServer || !this.isMounted) return
      try {
        // 获取当前语言设置
        const currentLang = this.getCurrentLanguage()

        // 只有中文时才加载中文语言包
        let langModule = null
        if (currentLang === 'zh-CN' || currentLang === 'zh_CN' || currentLang === 'zh') {
          langModule = await import('@toast-ui/editor/dist/i18n/zh-cn')
        }

        // 2. 引入颜色插件
        const colorSyntax = (await import('@toast-ui/editor-plugin-color-syntax')).default
        await import('tui-color-picker/dist/tui-color-picker.css') // 添加颜色选择器样式

        // 引入所需的样式文件
        await Promise.all([
          import('@toast-ui/editor/dist/toastui-editor.css'),
          import('@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css')
        ])
        // 3. 加载编辑器
        const Editor = (await import('@toast-ui/editor')).default

        // 4. 根据语言设置编辑器配置
        const editorConfig = {
          el: this.$refs.editorContainer,
          height: this.height,
          initialValue: this.value,
          previewStyle: 'vertical',
          initialEditType: 'wysiwyg',
          usageStatistics: false,
          plugins: [
            colorSyntax
          ],
          toolbarItems: [
            ['bold', 'italic', 'strike'],
            ['hr', 'quote'],
            ['ul', 'ol', 'task', 'indent', 'outdent'],
            ['table', 'image', 'link'],
            ['code', 'codeblock']
          ],
          events: {
            change: () => {
              const content = this.editor.getMarkdown()
              this.$emit('input', content)
              this.$emit('change', content)
            }
          },
          hooks: {
            addImageBlobHook: async (blob, callback) => {
              try {
                const formData = new FormData()
                formData.append('file', blob)
                formData.append('biz', 'editor') // 添加biz参数

                const res = await uploadFile(formData)
                console.log('图片上传响应:', res)
                if (res.success) {
                  // 上传成功，返回图片URL
                  const url = this.STATIC_BASE_URL + this.STATIC_FILE_SUFFIX + res.message
                  console.log('生成的图片URL:', url)
                  callback(url)
                } else {
                  console.error('上传失败，响应:', res)
                  this.$message.error('图片上传失败')
                }
              } catch (error) {
                console.error('Upload error:', error)
                this.$message.error('图片上传失败')
              }
            }
          }
        }

        // 只有中文时才设置语言
        if (currentLang === 'zh-CN' || currentLang === 'zh_CN' || currentLang === 'zh') {
          editorConfig.language = 'zh-CN'
          editorConfig.locale = 'zh-CN'
        }

        this.editor = new Editor(editorConfig)
        this.editor.insertToolbarItem({ groupIndex: 0, itemIndex: 0 }, {
          el: this.createHeadingButton(),
          name: 'myHeading',
          tooltip: this.getTooltipText(),
          command: 'heading',
          text: 'H',
          className: 'heading toastui-editor-toolbar-icons'
        })

        // 编辑器初始化完成后，再次确保tooltip正确设置
        this.$nextTick(() => {
          const headingButton = this.$refs.editorContainer.querySelector('.heading')
          if (headingButton) {
            const tooltipText = this.getTooltipText()
            headingButton.setAttribute('title', tooltipText)
            // 如果有aria-label也更新
            headingButton.setAttribute('aria-label', tooltipText)
          }
        })
      } catch (error) {
        console.error('Editor initialization failed:', error)
      }
    },
    // 创建标题下拉按钮
    createHeadingButton() {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'heading toastui-editor-toolbar-icons'
      button.style.margin = '0'

      const dropdown = document.createElement('ul')
      dropdown.className = 'tui-heading-dropdown'
      // dropdown.style.display = 'block'
      dropdown.style.display = 'none' // 初始状态改为none
      dropdown.style.zIndex = '1000'
      dropdown.style.position = 'absolute'
      dropdown.style.marginTop = '2px'
      dropdown.style.padding = '10px'
      dropdown.style.backgroundColor = 'white'
      dropdown.style.border = '1px solid #ccc'
      dropdown.style.borderRadius = '2px'
      dropdown.style.boxShadow = '0 2px 4px rgba(0,0,0,.12)'

      // 获取编辑器的翻译文本，如果没有翻译则使用默认文本
      const getHeadingText = (level) => {
        if (this.editor && this.editor.i18n) {
          // 尝试多种可能的翻译键格式
          const possibleKeys = [
            `Heading ${level}`,
            `heading${level}`,
            `Heading${level}`,
            `H${level}`,
            `heading ${level}`
          ]

          for (const key of possibleKeys) {
            try {
              const translatedText = this.editor.i18n.get(key)
              if (translatedText && translatedText !== key && translatedText.trim() !== '') {
                return translatedText
              }
            } catch (e) {
              // 忽略错误，继续尝试下一个键
            }
          }
        }

        // 如果没有翻译，根据当前语言使用默认文本
        const currentLang = this.getCurrentLanguage()
        const isChinese = currentLang === 'zh-CN' || currentLang === 'zh_CN' || currentLang === 'zh'

        if (isChinese) {
          const defaultTexts = {
            2: '二级标题',
            3: '三级标题',
            4: '四级标题'
          }
          return defaultTexts[level] || `${level}级标题`
        } else {
          return `Heading ${level}`
        }
      }

      const headings = [
        { text: getHeadingText(2), level: 2 },
        { text: getHeadingText(3), level: 3 },
        { text: getHeadingText(4), level: 4 }
      ]

      headings.forEach(heading => {
        const item = document.createElement('li')
        item.textContent = heading.text
        item.style.padding = '2px 10px'
        item.style.color = '#262626'
        item.style.cursor = 'pointer'
        item.style.listStyleType = 'none'

        // 按级别设置样式
        if (heading.level === 2) {
          item.style.fontSize = '24px'
          item.style.fontWeight = 'bold'
        } else if (heading.level === 3) {
          item.style.fontSize = '18px'
          item.style.fontWeight = 'bold'
        } else if (heading.level === 4) {
          item.style.fontSize = '16px'
          item.style.fontWeight = 'bold'
        }

        item.addEventListener('mouseover', () => {
          item.style.backgroundColor = '#f5f5f5'
        })

        item.addEventListener('mouseout', () => {
          item.style.backgroundColor = 'white'
        })

        item.addEventListener('click', (e) => {
          e.stopPropagation() // 阻止事件冒泡
          this.editor.exec('heading', { level: heading.level })
          dropdown.style.display = 'none'
        })

        dropdown.appendChild(item)
      })

      button.addEventListener('click', (e) => {
        e.stopPropagation()
        dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none'
      })

      // document.addEventListener('click', () => {
      //   dropdown.style.display = 'none'
      // })
      // 将点击事件监听器添加到document.body而不是document
      document.body.addEventListener('click', (e) => {
        if (!button.contains(e.target)) {
          dropdown.style.display = 'none'
        }
      })

      button.appendChild(dropdown)
      return button
    },
    getTooltipText() {
      console.log('MarkdownEditor - 获取tooltip文本')

      // 获取标题按钮的翻译文本
      if (this.editor && this.editor.i18n) {
        console.log('MarkdownEditor - 尝试从编辑器i18n获取翻译')
        // 尝试多种可能的翻译键
        const possibleKeys = [
          'Heading',
          'heading',
          'Headers',
          'headers',
          'Title'
        ]

        for (const key of possibleKeys) {
          try {
            const translatedText = this.editor.i18n.get(key)
            if (translatedText && translatedText !== key && translatedText.trim() !== '') {
              console.log(`MarkdownEditor - 找到翻译文本: ${key} = ${translatedText}`)
              return translatedText
            }
          } catch (e) {
            // 忽略错误，继续尝试下一个键
          }
        }
      }

      // 如果没有翻译，根据当前语言使用默认文本
      const currentLang = this.getCurrentLanguage()
      const isChinese = currentLang === 'zh-CN' || currentLang === 'zh_CN' || currentLang === 'zh'

      const tooltipText = isChinese ? '标题' : 'Heading'
      console.log(`MarkdownEditor - 使用默认tooltip文本: ${tooltipText} (语言: ${currentLang})`)

      return tooltipText
    },
    getCurrentLanguage() {
      // 优先使用Cookie中的语言设置
      const cookieLang = Cookies.get('DEFAULT_LANG')
      console.log('MarkdownEditor - Cookie语言:', cookieLang)

      if (cookieLang) {
        return cookieLang
      }

      // 其次使用全局语言设置
      if (this.globalDataArray && this.globalDataArray.siteInfo && this.globalDataArray.siteInfo.language) {
        console.log('MarkdownEditor - 全局语言:', this.globalDataArray.siteInfo.language)
        return this.globalDataArray.siteInfo.language
      }

      // 如果都没有，尝试从store获取
      if (this.$store && this.$store.state && this.$store.state.globalDataArray && this.$store.state.globalDataArray.siteInfo) {
        console.log('MarkdownEditor - Store语言:', this.$store.state.globalDataArray.siteInfo.language)
        return this.$store.state.globalDataArray.siteInfo.language
      }

      console.log('MarkdownEditor - 使用默认语言: zh_CN')
      // 默认返回中文
      return 'zh_CN'
    },
    updateTooltip() {
      console.log('MarkdownEditor - 更新tooltip')
      if (this.editor && this.$refs.editorContainer) {
        this.$nextTick(() => {
          const headingButton = this.$refs.editorContainer.querySelector('.heading')
          if (headingButton) {
            const tooltipText = this.getTooltipText()
            headingButton.setAttribute('title', tooltipText)
            headingButton.setAttribute('aria-label', tooltipText)
            console.log(`MarkdownEditor - tooltip已更新为: ${tooltipText}`)
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
.markdown-editor {
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;

  ::v-deep {
    // CSS已在initEditor方法中动态导入，无需静态导入
    // 自定义工具栏样式
    .toastui-editor-defaultUI {
      border: none;

      .toastui-editor-toolbar {
        background: #fafafa;
        padding: 6px;

        button {
          border-radius: 2px;
          margin: 0 2px;

          &:hover {
            background: #f0f0f0;
          }
        }
      }

      // 内容区域样式
      .toastui-editor-main {
        .toastui-editor-md-container,
        .toastui-editor-md-preview {
          padding: 12px;
        }
      }
    }

    // 自定义颜色选择器样式
    .color-dropdown {
      .color-palette {
        display: flex;
        flex-wrap: wrap;
        width: 180px;
        padding: 8px;

        button {
          width: 20px;
          height: 20px;
          margin: 2px;
          border-radius: 2px;
          border: 1px solid #eee;

          &:hover {
            transform: scale(1.1);
          }
        }
      }
    }
  }
}
</style>
